import React, { useEffect, useState } from "react";
import {
  fetchSeasonPassState, adminSetSeasonPassState, fetchSeasonThemes,
} from "../../lib/api";
import { refreshSeasonPassFlag } from "../../hooks/useSeasonPassFlag";
import { SeasonPassBanner } from "../SeasonPassBanner";
import { SeasonTierEditor } from "./SeasonTierEditor";

/**
 * SeasonPassSection — Wave D + E admin editor for the platform-wide
 * Season Pass: promo banner theming + XP rules + tier rewards.
 */
export function SeasonPassSection({ onToast }) {
  const [state, setState] = useState(null);
  const [themes, setThemes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);   // working draft (not yet saved)
  const [endsAt, setEndsAt] = useState("");

  useEffect(() => {
    Promise.all([fetchSeasonPassState(), fetchSeasonThemes()])
      .then(([s, t]) => {
        setState(s); setThemes(t);
        setForm({
          theme: s.theme || "neon",
          label: s.label || "",
          tagline: s.tagline || "",
          emoji: s.emoji || "⚡",
          accent: s.accent || "violet",
          cta_label: s.cta_label || "",
          cta_url: s.cta_url || "",
          promo_code: s.promo_code || "",
        });
        if (s.ends_at) {
          try {
            const d = new Date(s.ends_at);
            const pad = (n) => String(n).padStart(2, "0");
            setEndsAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
          } catch { /* ignore */ }
        }
      })
      .catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPreset = (preset) => {
    setForm({
      theme: preset.id,
      label: preset.label,
      tagline: preset.tagline,
      emoji: preset.emoji,
      accent: preset.accent,
      cta_label: preset.cta_label || "",
      cta_url: preset.cta_url || "",
      promo_code: preset.promo_code || "",
    });
    onToast?.(`Loaded preset: ${preset.label}`);
  };

  const save = async (enabled) => {
    setBusy(true);
    try {
      const body = { ...form, enabled };
      if (endsAt) body.ends_at = new Date(endsAt + "T23:59:59Z").toISOString();
      else body.ends_at = null;
      const fresh = await adminSetSeasonPassState(body);
      setState(fresh);
      refreshSeasonPassFlag();
      onToast?.(enabled ? "Season Pass ACTIVATED platform-wide." : "Season Pass updated.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const fresh = await adminSetSeasonPassState({ enabled: false });
      setState(fresh);
      refreshSeasonPassFlag();
      onToast?.("Season Pass deactivated.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't deactivate.");
    } finally { setBusy(false); }
  };

  if (!state || !form) return <p className="text-white/45 text-sm">Loading…</p>;

  const ACCENTS = ["violet", "red", "green", "amber", "orange", "cyan", "pink"];

  return (
    <section data-testid="admin-season-section" className="space-y-5">
      {/* ── Status strip ───────────────────────────────────────── */}
      <div className="rounded-2xl p-4 border border-[#14b8a6]/30 bg-[#14b8a6]/5 flex items-center gap-3 flex-wrap">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-bolt mr-1" />FEATURE FLAG STATUS
        </p>
        <span data-testid="admin-season-status"
              className={`text-[10px] tracking-widest px-2 py-1 rounded-md font-bold ${state.enabled
                ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                : "bg-white/5 text-white/55 border border-white/15"}`}>
          {state.enabled ? "● ENABLED" : "○ DISABLED"}
        </span>
        {state.updated_at && (
          <span className="text-[10px] text-white/35 ml-auto">
            updated {new Date(state.updated_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* ── Preset chips ───────────────────────────────────────── */}
      <div>
        <p className="text-[10px] tracking-[3px] text-[#a3a3ac] font-bold mb-2">
          PROMO PRESETS — START FROM A TEMPLATE
        </p>
        <div className="flex flex-wrap gap-2" data-testid="admin-season-preset-chips">
          {themes.map((t) => (
            <button
              type="button" key={t.id}
              onClick={() => applyPreset(t)}
              data-testid={`admin-season-preset-${t.id}`}
              className={`text-[11px] tracking-widest px-3 py-1.5 rounded-full border transition ${
                form.theme === t.id
                  ? "bg-[#14b8a6]/25 border-[#14b8a6] text-[#14b8a6]"
                  : "bg-black/30 border-white/15 text-white/75 hover:border-white/40"
              }`}>
              {t.emoji} {t.id.replace("_", " ").toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ── Editor ─────────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-2xl p-5 bg-black/40 border border-white/10 space-y-3">
          <p className="text-[10px] tracking-[3px] text-[#a3a3ac] font-bold">EDIT CONTENT</p>
          <Field label="LABEL" testId="admin-season-label-input"
                 value={form.label} onChange={(v) => setField("label", v)}
                 placeholder="BLACK FRIDAY · 48 HOURS ONLY" maxLength={80} />
          <Field label="TAGLINE" testId="admin-season-tagline-input"
                 value={form.tagline} onChange={(v) => setField("tagline", v)}
                 placeholder="PRO subscription 50% off…" maxLength={240} textarea />
          <div className="grid grid-cols-2 gap-2">
            <Field label="EMOJI" testId="admin-season-emoji-input"
                   value={form.emoji} onChange={(v) => setField("emoji", v)}
                   placeholder="🛒" maxLength={4} />
            <label className="block">
              <span className="text-[10px] tracking-widest text-[#a3a3ac]">ACCENT</span>
              <select value={form.accent} onChange={(e) => setField("accent", e.target.value)}
                      data-testid="admin-season-accent-input"
                      className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm">
                {ACCENTS.map((a) => <option key={a} value={a}>{a.toUpperCase()}</option>)}
              </select>
            </label>
          </div>
          <Field label="CTA BUTTON LABEL (OPTIONAL)" testId="admin-season-cta-label-input"
                 value={form.cta_label} onChange={(v) => setField("cta_label", v)}
                 placeholder="CLAIM 50% OFF PRO" maxLength={40} />
          <Field label="CTA URL (OPTIONAL · path or https://)" testId="admin-season-cta-url-input"
                 value={form.cta_url} onChange={(v) => setField("cta_url", v)}
                 placeholder="/pricing?promo=BF50" maxLength={240} />
          <Field label="PROMO CODE (OPTIONAL · auto-uppercased)" testId="admin-season-promo-input"
                 value={form.promo_code} onChange={(v) => setField("promo_code", v.toUpperCase())}
                 placeholder="BF50" maxLength={24} />
          <label className="block">
            <span className="text-[10px] tracking-widest text-[#a3a3ac]">ENDS AT (OPTIONAL)</span>
            <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
                   data-testid="admin-season-endsat-input"
                   className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm" />
          </label>
        </div>

        {/* ── Live Preview ───────────────────────────────────────── */}
        <div className="space-y-3">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-eye mr-1" />LIVE PREVIEW
          </p>
          <div data-testid="admin-season-preview">
            <SeasonPassBanner
              override={{
                enabled: true,
                label: form.label || "Season Label",
                tagline: form.tagline,
                emoji: form.emoji,
                accent: form.accent,
                cta_label: form.cta_label,
                cta_url: form.cta_url || "/pricing",
                promo_code: form.promo_code,
                ends_at: endsAt ? new Date(endsAt + "T23:59:59Z").toISOString() : null,
              }}
            />
          </div>
          <p className="text-[10px] text-white/45">
            Preview reflects unsaved changes. Click ACTIVATE / SAVE to push live.
          </p>
        </div>
      </div>

      {/* ── Actions ────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 pt-1">
        {state.enabled ? (
          <>
            <button type="button" onClick={() => save(true)} disabled={busy}
                    data-testid="admin-season-save-btn"
                    className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50">
              {busy ? "…" : <><i className="fas fa-floppy-disk mr-1" />SAVE CHANGES</>}
            </button>
            <button type="button" onClick={disable} disabled={busy}
                    data-testid="admin-season-disable-btn"
                    className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-ghost disabled:opacity-50">
              {busy ? "…" : "DEACTIVATE"}
            </button>
          </>
        ) : (
          <button type="button" onClick={() => save(true)} disabled={busy}
                  data-testid="admin-season-enable-btn"
                  className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50">
            {busy ? "…" : <><i className="fas fa-bolt mr-1" />ACTIVATE PLATFORM-WIDE</>}
          </button>
        )}
      </div>

      {/* ── Tier + XP Rule editor ──────────────────────────────── */}
      <SeasonTierEditor onToast={onToast} />
    </section>
  );
}

function Field({ label, value, onChange, placeholder, maxLength, textarea, testId }) {
  const Common = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-[10px] tracking-widest text-[#a3a3ac]">{label}</span>
      <Common
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={textarea ? 3 : undefined}
        data-testid={testId}
        className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30"
      />
    </label>
  );
}

